import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, CircularProgress, Alert, Chip, Card, CardContent, Grid,
    TextField, InputAdornment, Tooltip, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon, Error as ErrorIcon, Search as SearchIcon,
    Close as CloseIcon, AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { daysFromStageDate, getDODays } from '../../../utils/tatRules';
import { uploadFile } from '../../../utils/s3Utils';

const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? '—' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getAllowed = (row) => row.AllowedDays != null ? row.AllowedDays : getDODays(row);

const getRowStatus = (row) => {
    if (row.IsResolved) return 'resolved';
    const days = daysFromStageDate(row.StageDate);
    if (days === null) return 'ok';
    return days > getAllowed(row) ? 'overshoot' : 'ok';
};

const statusCfg = {
    overshoot: { color: '#b71c1c', bg: '#ffcdd2', label: 'Overshoot', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
    ok:        { color: '#e65100', bg: '#fff8e1', label: 'Pending',   icon: null },
    resolved:  { color: '#2e7d32', bg: '#e8f5e9', label: 'Resolved',  icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
};

// ── Resolve Dialog ────────────────────────────────────────────────────────────
const ResolveDialog = ({ open, row, onClose, onSuccess }) => {
    const [remarks, setRemarks] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) { setRemarks(''); setFile(null); setError(''); }
    }, [open]);

    const handleSubmit = async () => {
        if (!remarks.trim()) { setError('Remarks are mandatory'); return; }
        setUploading(true); setError('');
        try {
            let attachmentUrl = null;
            if (file) {
                const upload = await uploadFile(file, 'tat-overshoot');
                if (!upload.success) throw new Error('File upload failed');
                attachmentUrl = upload.url;
            }

            const token = localStorage.getItem('authToken');
            const doId = localStorage.getItem('orgId');
            const res = await fetch(`${process.env.REACT_APP_API_URL}/do/tat-overshoot/resolve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
                body: JSON.stringify({ id: row.Id, remarks: remarks.trim(), attachmentUrl, doId }),
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.message || 'Resolve failed');
            onSuccess();
        } catch (e) { setError(e.message); }
        finally { setUploading(false); }
    };

    if (!row) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#00024c' }}>Reason for Overshoot</Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>{row.ApplicationNo} — {row.LaboratoryName}</Typography>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TextField
                    label="Please specify the reason for the delay.*"
                    multiline
                    rows={4}
                    fullWidth
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                    placeholder="Enter remarks explaining the resolution…"
                    error={!remarks.trim() && !!error}
                  
                />
                <Typography variant='subtitle2' sx={{ mb: 2 }} >Please submit this only after the case has proceeded to the next stage.</Typography>

                <Box>
                    <Typography variant="body2" sx={{ mb: 1, color: '#555' }}>
                        Attachment <span style={{ color: '#888' }}>(optional)</span>
                    </Typography>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<AttachFileIcon />}
                        size="small"
                        sx={{ mr: 2 }}
                    >
                        {file ? file.name : 'Choose File'}
                        <input type="file" hidden onChange={e => setFile(e.target.files[0] || null)} />
                    </Button>
                    {file && (
                        <Typography variant="caption" sx={{ color: '#555' }}>
                            {(file.size / 1024).toFixed(1)} KB
                        </Typography>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={uploading}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={uploading || !remarks.trim()}
                    sx={{ background: '#00024c', '&:hover': { background: '#1a237e' } }}
                >
                    {uploading ? 'Submitting…' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
const TATOvershoot = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [cardFilter, setCardFilter] = useState('');
    const [dialogRow, setDialogRow] = useState(null);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setLoading(true); setError(null);
        try {
            const token = localStorage.getItem('authToken');
            const doId = localStorage.getItem('orgId');
            const res = await fetch(`${process.env.REACT_APP_API_URL}/do/tat-overshoot`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
                body: JSON.stringify({ doId }),
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.message || 'Fetch failed');
            setData(json.data || []);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    const searchFiltered = data.filter(r => {
        const q = search.toLowerCase();
        return !q ||
            (r.ApplicationNo || '').toLowerCase().includes(q) ||
            (r.LaboratoryName || '').toLowerCase().includes(q) ||
            (r.JDName || '').toLowerCase().includes(q) ||
            (r.DoName || '').toLowerCase().includes(q);
    });

    const filtered = searchFiltered.filter(r => {
        if (!cardFilter) return true;
        const s = getRowStatus(r);
        if (cardFilter === 'overshoot') return s === 'overshoot';
        if (cardFilter === 'resolved') return s === 'resolved';
        return true;
    });

    const overshootCount = data.filter(r => getRowStatus(r) === 'overshoot').length;
    const resolvedCount  = data.filter(r => r.IsResolved).length;

    const showResolved = cardFilter === 'resolved';

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', mb: 1 }}>
                Turn Around Time Overshoot
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                Days from StageDate vs allowed DO days (per NABL TAT document).
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                    { label: 'Overshoot', value: overshootCount, color: '#b71c1c', bg: '#ffcdd2', key: 'overshoot', hint: 'Click to show overshoot applications' },
                    { label: 'Resolved',  value: resolvedCount,  color: '#2e7d32', bg: '#e8f5e9', key: 'resolved',  hint: 'Click to show resolved applications' },
                ].map(item => (
                    <Grid item xs={6} sm={3} key={item.label}>
                        <Card
                            onClick={() => setCardFilter(f => f === item.key ? '' : item.key)}
                            sx={{
                                border: cardFilter === item.key ? `2px solid ${item.color}` : `1px solid ${item.color}44`,
                                background: item.bg,
                                cursor: 'pointer',
                                boxShadow: cardFilter === item.key ? `0 0 0 2px ${item.color}55` : undefined,
                                transition: 'box-shadow 0.15s, border 0.15s',
                            }}
                        >
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 1 } }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: item.color }}>{item.value}</Typography>
                                <Typography variant="caption" sx={{ color: item.color, display: 'block' }}>{item.label}</Typography>
                                <Typography variant="caption" sx={{ color: item.color, opacity: 0.7, fontSize: '0.65rem' }}>{item.hint}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <TextField
                size="small"
                placeholder="Search App No, Lab Name, JD, DO…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ mb: 2, width: 360 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && !error && (
                <TableContainer component={Paper} sx={{ maxHeight: 560, overflowX: 'auto' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { background: '#1a237e', color: '#fff', fontWeight: 600, fontSize: '0.77rem', whiteSpace: 'nowrap' } }}>
                                <TableCell># / Action</TableCell>
                                <TableCell>Application</TableCell>
                                <TableCell>Lab Type / Assessment</TableCell>
                                <TableCell>DO / JD</TableCell>
                                <TableCell>Stage Description</TableCell>
                                <TableCell>Stage Date</TableCell>
                                <TableCell sx={{ background: '#b71c1c !important' }}>Allowed Days</TableCell>
                                <TableCell sx={{ background: '#b71c1c !important' }}>Days at Stage</TableCell>
                                {showResolved && <TableCell sx={{ background: '#2e7d32 !important' }}>Reason for Overshoot</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow><TableCell colSpan={showResolved ? 9 : 8} align="center" sx={{ py: 4, color: '#888' }}>No records found</TableCell></TableRow>
                            )}
                            {filtered.map((row, i) => {
                                const days = daysFromStageDate(row.StageDate);
                                const allowed = getAllowed(row);
                                const rowStatus = getRowStatus(row);
                                const cfg = statusCfg[rowStatus];
                                return (
                                    <TableRow key={i} sx={{ background: cfg.bg }}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#555' }}>{i + 1}</Typography>
                                            {!row.IsResolved ? (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => setDialogRow(row)}
                                                    sx={{
                                                        fontSize: '0.7rem', px: 1.5, py: 0.5,
                                                        background: '#00024c',
                                                        '&:hover': { background: '#1a237e' },
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    Resolve
                                                </Button>
                                            ) : (
                                                <Tooltip title={`Resolved on ${formatDate(row.ResolvedDate)}`}>
                                                    <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                                        ✓ {formatDate(row.ResolvedDate)}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 180 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#00024c', display: 'block' }}>{row.ApplicationNo || '—'}</Typography>
                                            <Tooltip title={row.AddressLine1 || ''}><span>{row.LaboratoryName || '—'}</span></Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{row.LabType || '—'}</Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>{row.AssessmentName || '—'}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{row.DoName || '—'}</Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>{row.JDName || '—'}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200 }}>{row.stageDescription || row.Stage || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatDate(row.StageDate)}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', color: '#555' }}>{allowed} days</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: cfg.color }}>
                                            {days !== null ? `${days} day${days !== 1 ? 's' : ''}` : '—'}
                                        </TableCell>
                                        {showResolved && (
                                            <TableCell sx={{ fontSize: '0.75rem', minWidth: 220 }}>
                                                {row.IsResolved ? (
                                                    <Box>
                                                        <Typography variant="caption" sx={{ display: 'block' }}><strong>Date:</strong> {formatDate(row.ResolvedDate)}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block' }}><strong>By:</strong> {row.resolvedbyName || '—'}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block' }}><strong>Remarks:</strong> {row.ResolvedRemarks || '—'}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                            <strong>Attachment:</strong>{' '}
                                                            {row.ResolvedAttachment
                                                                ? <a href={row.ResolvedAttachment} target="_blank" rel="noreferrer" style={{ color: '#1a237e', fontWeight: 600 }}>View</a>
                                                                : '—'}
                                                        </Typography>
                                                    </Box>
                                                ) : '—'}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ResolveDialog
                open={!!dialogRow}
                row={dialogRow}
                onClose={() => setDialogRow(null)}
                onSuccess={() => { setDialogRow(null); fetchData(); }}
            />
        </Box>
    );
};

export default TATOvershoot;
